import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany } from "@/lib/company";
import { COUNTRIES, CURRENCIES, TAX_REGIMES, statesFor, countryDefaults } from "@/lib/geoData";

export default function CompanySetup() {
  const navigate = useNavigate();
  const { refresh, setActive } = useCompany();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "",
    country: "IN",
    base_currency: "INR",
    tax_regime: "IN_GST",
    gstin: "",
    vat_id: "",
    state: "",
    state_code: "",
    city: "",
    postal_code: "",
    address_line1: "",
    fiscal_year_start_month: 4,
    invoice_prefix: "INV",
  });

  const onCountryChange = (code) => {
    const c = countryDefaults(code);
    setF((s) => ({
      ...s,
      country: code,
      base_currency: c?.currency || "USD",
      tax_regime: c?.regime || "NONE",
      fiscal_year_start_month: code === "IN" ? 4 : 1,
      state: "",
      state_code: "",
      gstin: code === "IN" ? s.gstin : "",
      vat_id: code !== "IN" ? s.vat_id : "",
    }));
  };

  const states = statesFor(f.country);
  const isGst = f.tax_regime === "IN_GST";
  const isVat = ["AE_VAT", "EU_VAT", "UK_VAT"].includes(f.tax_regime);

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/companies", f);
      toast.success("Company created!");
      await refresh();
      setActive(data.id);
      navigate("/app/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create company");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="company-setup-page">
      <div className="mb-6">
        <div className="overline mb-1">Setup</div>
        <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] flex items-center gap-2">
          <Building2 size={24}/> Create Company
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          Configure your business identity, country, currency, and tax regime.
        </p>
      </div>

      {/* Step 1 — Basics */}
      <div className="card-surface p-4 sm:p-6 mb-4">
        <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-[#1E3A8A] text-white text-xs flex items-center justify-center">1</span>
          Company Basics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-field">Company Name *</label>
            <input
              data-testid="setup-name" className="input-clean"
              value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
              placeholder="Your Company Pvt Ltd"
            />
          </div>
          <div>
            <label className="label-field">Country *</label>
            <select
              data-testid="setup-country" className="input-clean"
              value={f.country} onChange={(e) => onCountryChange(e.target.value)}
            >
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-field">Base Currency *</label>
            <select data-testid="setup-currency" className="input-clean"
              value={f.base_currency}
              onChange={(e) => setF({ ...f, base_currency: e.target.value })}
            >
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-field">Tax Regime *</label>
            <select data-testid="setup-regime" className="input-clean"
              value={f.tax_regime}
              onChange={(e) => setF({ ...f, tax_regime: e.target.value })}
            >
              {TAX_REGIMES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Step 2 — Tax IDs & Address */}
      <div className="card-surface p-4 sm:p-6 mb-4">
        <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-[#0F172A] text-white text-xs flex items-center justify-center">2</span>
          Tax IDs & Address
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isGst && (
            <div>
              <label className="label-field">GSTIN</label>
              <input data-testid="setup-gstin" className="input-clean font-mono-data"
                value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })}
                placeholder="27ABCDE1234F1Z5" maxLength={15}
              />
            </div>
          )}
          {isVat && (
            <div>
              <label className="label-field">VAT / Tax ID</label>
              <input data-testid="setup-vat" className="input-clean font-mono-data"
                value={f.vat_id} onChange={(e) => setF({ ...f, vat_id: e.target.value })}
                placeholder="e.g. GB123456789"
              />
            </div>
          )}
          <div>
            <label className="label-field">State / Region {isGst && "*"}</label>
            {states.length > 0 ? (
              <select
                data-testid="setup-state" className="input-clean"
                value={f.state_code}
                onChange={(e) => {
                  const st = states.find(([code]) => code === e.target.value);
                  setF({ ...f, state_code: e.target.value, state: st ? st[1] : "" });
                }}
              >
                <option value="">Select state…</option>
                {states.map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            ) : (
              <input data-testid="setup-state" className="input-clean"
                value={f.state} onChange={(e) => setF({ ...f, state: e.target.value, state_code: e.target.value })}
              />
            )}
          </div>
          <div>
            <label className="label-field">City</label>
            <input data-testid="setup-city" className="input-clean" value={f.city}
              onChange={(e) => setF({ ...f, city: e.target.value })}/>
          </div>
          <div>
            <label className="label-field">Postal Code</label>
            <input data-testid="setup-postal" className="input-clean" value={f.postal_code}
              onChange={(e) => setF({ ...f, postal_code: e.target.value })}/>
          </div>
          <div className="md:col-span-2">
            <label className="label-field">Address Line 1</label>
            <input data-testid="setup-address" className="input-clean" value={f.address_line1}
              onChange={(e) => setF({ ...f, address_line1: e.target.value })}/>
          </div>
        </div>
      </div>

      {/* Step 3 — Invoice settings */}
      <div className="card-surface p-4 sm:p-6 mb-6">
        <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-[#1E3A8A] text-white text-xs flex items-center justify-center">3</span>
          Invoice Settings
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-field">Invoice Prefix</label>
            <input data-testid="setup-prefix" className="input-clean font-mono-data"
              value={f.invoice_prefix} maxLength={10}
              onChange={(e) => setF({ ...f, invoice_prefix: e.target.value.toUpperCase() })}/>
          </div>
          <div>
            <label className="label-field">Fiscal Year Start Month</label>
            <select data-testid="setup-fy" className="input-clean"
              value={f.fiscal_year_start_month}
              onChange={(e) => setF({ ...f, fiscal_year_start_month: parseInt(e.target.value, 10) })}
            >
              {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
        <button
          data-testid="setup-cancel-btn" className="btn-ghost w-full sm:w-auto justify-center"
          onClick={() => navigate(-1)}
        >
          Cancel
        </button>
        <button
          data-testid="setup-submit-btn" className="btn-primary w-full sm:w-auto justify-center" disabled={saving} onClick={submit}
        >
          {saving ? "Creating…" : <><Check size={14}/> Create Company <ArrowRight size={14}/></>}
        </button>
      </div>
    </div>
  );
}
